import * as T from 'three';
import {GLTFLoader} from 'three/addons/loaders/GLTFLoader.js';
import {clone as cloneSkeleton} from 'three/addons/utils/SkeletonUtils.js';
import {DEFAULT_AVATAR} from './world-seeds.mjs';

import {AVATAR_MODELS,avatarPreset} from './avatar-metadata.mjs';
export {AVATAR_MODELS,avatarPreset} from './avatar-metadata.mjs';
const FIELD_FOR_MATERIAL={Jacket:'color',JacketAccent:'accent',Skin:'skin',Hair:'hair',Brow:'hair'};
const clamp=(v,min,max)=>Math.min(max,Math.max(min,v));
const validColor=v=>typeof v==='string'&&/^#[0-9a-f]{6}$/i.test(v);

// Load only requested presets. Player, portrait and remote instances share a
// promise and immutable source asset; they never share mutable skeletons.
export function createAvatarModelLibrary(fetchModel=(url)=>new GLTFLoader().loadAsync(url)){
  const loaded=new Map(),pending=new Map();
  return {
    peek(preset){return loaded.get(avatarPreset(preset));},
    load(preset){
      const key=avatarPreset(preset);
      if(loaded.has(key))return Promise.resolve(loaded.get(key));
      if(pending.has(key))return pending.get(key);
      const descriptor=AVATAR_MODELS.find(model=>model.value===key);
      const url=new URL(`./${descriptor.file}`,import.meta.url);
      if(descriptor.revision)url.searchParams.set('v',descriptor.revision);
      const promise=Promise.resolve().then(()=>fetchModel(url.href,key)).then(asset=>{
        if(!asset?.scene)throw new Error(`Invalid ${key} avatar asset`);
        loaded.set(key,asset);return asset;
      }).finally(()=>{if(pending.get(key)===promise)pending.delete(key);});
      pending.set(key,promise);return promise;
    },
  };
}
const modelLibrary=createAvatarModelLibrary();
export async function loadAvatars(preset='female'){
  try{await modelLibrary.load(avatarPreset(preset));return true;}
  catch(error){console.warn('Avatar asset unavailable',error);return false;}
}

// The saved default palette means neutral against each model's authored look.
export function avatarTint(field,value){
  const tint=new T.Color(1,1,1);
  if(!validColor(value)||!validColor(DEFAULT_AVATAR[field])||value.toLowerCase()===DEFAULT_AVATAR[field].toLowerCase())return tint;
  const chosen=new T.Color(value),base=new T.Color(DEFAULT_AVATAR[field]);
  tint.setRGB(clamp(chosen.r/Math.max(base.r,.025),.08,2.8),clamp(chosen.g/Math.max(base.g,.025),.08,2.8),clamp(chosen.b/Math.max(base.b,.025),.08,2.8));
  return tint;
}
export function avatarAnimationState(speed=0,airborne=false,previous='Idle',wave=false,hasWave=false,hasJog=false,options={}){
  if(options?.flying===true){
    if(options.hasFlightTriplet){
      const flightSpeed=Number.isFinite(options.flightSpeed)?Math.max(0,options.flightSpeed):Number.isFinite(speed)?Math.max(0,speed):0;
      if(flightSpeed<(previous==='FlightIdle'?.65:.3))return 'FlightIdle';
      return flightSpeed>(previous==='FlightFast'?13:14)?'FlightFast':'FlightMove';
    }
    return options.hasFlight?'Flight':'Jump';
  }
  if(airborne)return 'Jump';
  speed=Number.isFinite(speed)?Math.max(0,speed):0;
  if(wave&&hasWave&&speed<.35)return 'Wave';
  if(speed<(previous==='Idle'?.20:.12))return 'Idle';
  if(speed>(previous==='Run'?6.1:6.6))return 'Run';
  return hasJog&&speed>(previous==='Jog'?1.6:2.0)?'Jog':'Walk';
}

// Optional source/library injection supports deterministic tests. Ordinary
// callers use the shared lazy registry. root and body remain stable on switches.
export function createAvatar(config={},source,options={}){
  const library=options.library??modelLibrary;
  const suppliedPreset=avatarPreset(config.preset);
  const sourceFor=preset=>source?.scene&&preset===suppliedPreset?source:library.peek(preset);
  const root=new T.Group(),body=new T.Group();body.name='AvatarBody';root.add(body);
  root.userData.avatarModelRevision=0;body.userData.avatarUnavailable=true;
  const shadow=new T.Mesh(new T.CircleGeometry(.35,32),new T.MeshBasicMaterial({color:'#11121b',transparent:true,opacity:.22,depthWrite:false}));
  const shadowBaseMaterial=shadow.material;
  shadow.rotation.x=-Math.PI/2;shadow.position.y=.012;shadow.visible=false;root.add(shadow);
  let modelScene=null,mixer=new T.AnimationMixer(body),actions=new Map();
  let materialCopies=new Map(),originalColors=new Map(),originalEmissives=new Map(),hairChoices=[];
  let portalPose=null;const portalResidualMatrix=new T.Matrix4();
  let currentName='',currentAction=null,lastWave=false,lastAirborne=false,waveConsumed=false,lastWaveSequence=0;
  let currentPreset=null,requestedPreset=suppliedPreset,latestConfig={...config};
  let loading=false,loadError=null,failedPreset=null,pendingPreset=null,requestId=0,disposed=false;
  let ready=Promise.resolve(false),transaction=null,prepareId=0;
  // Keep the current and most recently used rig. Textures/geometry stay shared
  // with the immutable library; only skeletons/materials are instance-owned.
  const retainedModels=new Map();
  const cacheLimit=Math.max(1,Math.min(AVATAR_MODELS.length,options.modelCacheSize??2));

  function transition(next){
    if(!actions.has(next))next=actions.has('Idle')?'Idle':actions.keys().next().value;
    if(!next||next===currentName)return;
    const nextAction=actions.get(next),old=currentAction;
    const gait=name=>['Walk','Jog','Run'].includes(name);
    const flight=name=>['FlightIdle','FlightMove','FlightFast'].includes(name);
    const savedPhase=currentName==='PortalPose'?portalPose?.phase:null,phaseName=savedPhase?.name??currentName;
    const phase=old&&((gait(phaseName)&&gait(next))||(flight(phaseName)&&flight(next)))?(((savedPhase?.time??old.time)/Math.max(savedPhase?.duration??old.getClip().duration,.001))%1+1)%1:null;
    nextAction.reset().setEffectiveTimeScale(1).setEffectiveWeight(1).play();
    if(phase!==null)nextAction.time=phase*nextAction.getClip().duration;
    if(old)old.crossFadeTo(nextAction,flight(next)||flight(currentName)?.32:['LandSoft','Land'].includes(next)?.08:['Jump','Flight'].includes(next)?.09:['Jump','Flight'].includes(currentName)?.14:.18,false);
    currentAction=nextAction;currentName=next;
  }
  function clearPortalPose(){
    if(!portalPose)return;const previous=portalPose;portalPose=null;
    previous.action.stop();previous.mixer.uncacheClip(previous.clip);
    if(currentAction===previous.action){currentAction=null;currentName='';}
    for(const {object,auto}of previous.nodes)object.matrixAutoUpdate=auto;
  }
  function adoptPortalPose(bones,{preservePhase=false}={}){
    if(disposed||loading||!modelScene||!Array.isArray(bones)||!bones.length)throw Error('Loaded selected avatar pose required');
    const selected=new Set();body.traverse(object=>{if(object.isBone)selected.add(object);});
    if(bones.length!==selected.size||new Set(bones.map(({object})=>object)).size!==selected.size||bones.some(({object,world})=>!selected.has(object)||!world?.isMatrix4||!world.elements.every(Number.isFinite)))throw Error('Complete finite selected-avatar bone pose required');
    const phase=preservePhase&&currentAction?{name:currentName,time:currentAction.time,duration:currentAction.getClip().duration}:null;
    clearPortalPose();root.updateMatrixWorld(true);
    const byBone=new Map(bones.map(({object,world})=>[object,world])),nodes=[],tracks=[];
    body.traverse(object=>{if(!object.isBone)return;
      object.parent.updateWorldMatrix(true,false);
      const local=object.parent.matrixWorld.clone().invert().multiply(byBone.get(object));
      const auto=object.matrixAutoUpdate;object.matrix.copy(local);local.decompose(object.position,object.quaternion,object.scale);object.quaternion.normalize();
      const residual=new T.Matrix4().compose(object.position,object.quaternion,object.scale).invert().multiply(local);
      object.matrixAutoUpdate=false;object.matrixWorldNeedsUpdate=true;object.updateMatrixWorld(true);nodes.push({object,auto,residual});
      for(const [property,Track]of [['position',T.VectorKeyframeTrack],['quaternion',T.QuaternionKeyframeTrack],['scale',T.VectorKeyframeTrack]]){const values=object[property].toArray();tracks.push(new Track(object.name+'.'+property,[0,1],[...values,...values]));}
    });
    // Seed the existing selected-avatar mixer with the presented pose. Its
    // ordinary transition() owns the next walking/jump/flight crossfade.
    // No source actor, source animation, source clock or second mixer advances.
    for(const action of actions.values())action.stop();
    const clip=new T.AnimationClip('PortalPose',1,tracks),action=mixer.clipAction(clip);
    action.reset().setEffectiveTimeScale(1).setEffectiveWeight(1).play();
    currentAction=action;currentName='PortalPose';portalPose={clip,action,mixer,nodes,start:mixer.time,phase};
    mixer.update(0);root.updateMatrixWorld(true);return true;
  }
  function appearance(){
    const requested=Number(latestConfig.style??0);
    const selected=hairChoices.some(object=>object.userData.style_index===requested)?requested:0;
    for(const object of hairChoices)object.visible=object.userData.style_index===selected;
    for(const material of materialCopies.values()){
      const key=material.name.split('.')[0];
      const field=material.userData.avatar_color_field??(key.startsWith('Hair_')?'hair':FIELD_FOR_MATERIAL[key]);
      if(material.color)material.color.copy(originalColors.get(material));
      if(material.emissive&&originalEmissives.has(material))material.emissive.copy(originalEmissives.get(material));
      if(field){
        const tint=avatarTint(field,latestConfig[field]);
        material.color?.multiply(tint);material.emissive?.multiply(tint);
      }
    }
  }
  function modelRecord(){return {modelScene,mixer,actions,materialCopies,originalColors,originalEmissives,hairChoices};}
  function disposeRecord(record){
    record.mixer.stopAllAction();record.mixer.uncacheRoot(record.mixer.getRoot());
    const skeletons=new Set();record.modelScene?.traverse(object=>{if(object.isSkinnedMesh)skeletons.add(object.skeleton);});
    for(const skeleton of skeletons)skeleton.dispose();
    for(const material of record.materialCopies.values())material.dispose();
    record.modelScene?.removeFromParent();
  }
  function suspendModel(){
    clearPortalPose();
    if(!modelScene)return;
    mixer.stopAllAction();body.remove(modelScene);
    retainedModels.delete(currentPreset);retainedModels.set(currentPreset,modelRecord());
    modelScene=null;
  }
  function releaseModel(){
    clearPortalPose();
    if(modelScene)disposeRecord(modelRecord());
    for(const record of retainedModels.values())disposeRecord(record);
    retainedModels.clear();modelScene=null;actions=new Map();
    materialCopies=new Map();originalColors=new Map();originalEmissives=new Map();hairChoices=[];
  }
  function activate(record,preset,previousState){
    ({modelScene,mixer,actions,materialCopies,originalColors,originalEmissives,hairChoices}=record);
    body.add(modelScene);currentAction=null;currentName='';
    currentPreset=preset;body.userData.avatarUnavailable=false;body.userData.avatarPreset=preset;
    appearance();transition(previousState);mixer.update(0);
    shadow.visible=!lastAirborne;root.userData.avatarModelRevision++;
    while(retainedModels.size>=cacheLimit){
      const oldest=retainedModels.keys().next().value;
      disposeRecord(retainedModels.get(oldest));retainedModels.delete(oldest);
    }
  }
  function makeRecord(asset){
    const clone=cloneSkeleton(asset.scene),copies=new Map(),colors=new Map(),emissives=new Map(),choices=[];
    function ownMaterial(original){
      if(!copies.has(original)){
        const copy=original.clone();copies.set(original,copy);colors.set(copy,copy.color?.clone()??new T.Color(1,1,1));
        if(copy.emissive)emissives.set(copy,copy.emissive.clone());
      }
      return copies.get(original);
    }
    clone.traverse(object=>{
      if(object.userData.avatar_slot==='hair')choices.push(object);
      if(!object.isMesh)return;
      object.castShadow=true;object.receiveShadow=true;
      object.material=Array.isArray(object.material)?object.material.map(ownMaterial):ownMaterial(object.material);
      for(const material of Array.isArray(object.material)?object.material:[object.material])if(material.transparent)material.depthWrite=false;
    });
    // A prepared rig owns an independent mixer before it is attached. Binding
    // to its GLB root keeps staging and active animation paths identical.
    const nextMixer=new T.AnimationMixer(clone),nextActions=new Map();
    for(const clip of asset.animations??[]){
      const name=clip.name.split('|').at(-1),action=nextMixer.clipAction(clip);
      const oneShot=clip.userData?.loop!==true&&['Jump','JumpStart','LandSoft','Land','Wave'].includes(name);
      action.setLoop(oneShot?T.LoopOnce:T.LoopRepeat,Infinity);
      action.clampWhenFinished=oneShot;nextActions.set(name,action);
    }
    return {modelScene:clone,mixer:nextMixer,actions:nextActions,materialCopies:copies,originalColors:colors,originalEmissives:emissives,hairChoices:choices};
  }
  function install(asset,preset){
    const previousState=currentName||'Idle',retained=retainedModels.get(preset);
    retainedModels.delete(preset);
    const record=retained??makeRecord(asset);
    suspendModel();activate(record,preset,previousState);
  }
  /** Prepare without touching the visible selection, animation, or resources.
   * Commit is separate so external skeleton owners can rebind before an old
   * record is stopped/evicted. A failed load leaves the old avatar usable.
   */
  async function prepareCustomization(next={}){
    if(disposed)throw Error('Avatar disposed');
    if(transaction)throw Error('Avatar customization already active');
    const token=++prepareId,preset=avatarPreset(next.preset),baseRevision=root.userData.avatarModelRevision;
    const asset=sourceFor(preset)??await library.load(preset);
    if(disposed||token!==prepareId)throw Error('Avatar preparation cancelled');
    const record=makeRecord(asset),config={...next,preset};
    const stagedRoot=root.clone(false),stagedBody=body.clone(false),stagedShadow=shadow.clone(false),stagingScene=new T.Scene();
    stagedRoot.name='Prepared selected avatar';stagedShadow.material=shadowBaseMaterial;
    stagedRoot.add(stagedBody,stagedShadow);stagedBody.add(record.modelScene);stagingScene.add(stagedRoot);
    stagedRoot.userData.avatarModelRevision=baseRevision+1;stagedBody.userData.avatarUnavailable=false;stagedBody.userData.avatarPreset=preset;
    const stagedAvatar={root:stagedRoot,body:stagedBody,preset,requestedPreset:preset,loading:false,error:null,mixer:record.mixer,update(){}};
    const replacements=new Map([[stagedRoot,root],[stagedBody,body],[stagedShadow,shadow]]);
    let state='prepared',prior=null;const cleanupErrors=[];
    const releaseRecord=record=>{try{disposeRecord(record);}catch(error){cleanupErrors.push(error);}};
    const change={preset,baseRevision,stagedAvatar,replacements,
      activate(){
        if(disposed||state!=='prepared'||transaction||token!==prepareId||baseRevision!==root.userData.avatarModelRevision)throw Error('Stale avatar customization');
        clearPortalPose();
        prior={record:modelRecord(),currentPreset,requestedPreset,latestConfig,currentName,currentAction,loading,loadError,failedPreset,pendingPreset,revision:root.userData.avatarModelRevision,shadowVisible:shadow.visible};
        transaction=change;state='active';requestId++;pendingPreset=null;loading=false;loadError=null;failedPreset=null;
        modelScene?.removeFromParent();latestConfig=config;requestedPreset=preset;
        activate(record,preset,prior.currentName||'Idle');
        return true;
      },
      commit(){
        if(state!=='active'||transaction!==change)throw Error('Avatar customization is not active');
        transaction=null;state='committed';
        if(prior.record.modelScene){
          try{prior.record.mixer.stopAllAction();}catch(error){cleanupErrors.push(error);}
          if(retainedModels.has(prior.currentPreset))releaseRecord(retainedModels.get(prior.currentPreset));
          retainedModels.set(prior.currentPreset,prior.record);
        }
        while(retainedModels.size>=cacheLimit){const oldest=retainedModels.keys().next().value;releaseRecord(retainedModels.get(oldest));retainedModels.delete(oldest);}
        prior=null;ready=Promise.resolve(true);return true;
      },
      rollback(){
        if(state==='prepared'){state='cancelled';disposeRecord(record);return true;}
        if(state!=='active'||transaction!==change)return false;
        disposeRecord(record);({modelScene,mixer,actions,materialCopies,originalColors,originalEmissives,hairChoices}=prior.record);
        ({currentPreset,requestedPreset,latestConfig,currentName,currentAction,loading,loadError,failedPreset,pendingPreset}=prior);
        if(modelScene)body.add(modelScene);
        body.userData.avatarUnavailable=!modelScene;body.userData.avatarPreset=currentPreset;root.userData.avatarModelRevision=prior.revision;shadow.visible=prior.shadowVisible;
        appearance();root.updateMatrixWorld(true);transaction=null;state='rolled-back';prior=null;ready=Promise.resolve(!!modelScene);return true;
      },
      status:()=>state,cleanupErrors:()=>cleanupErrors.slice(),
    };
    return Object.freeze(change);
  }
  function customize(next={},retry=false){
    if(disposed)return Promise.resolve(false);
    if(transaction)return Promise.reject(Error('Avatar customization already active'));
    prepareId++;
    latestConfig={...next};requestedPreset=avatarPreset(next.preset);
    appearance();
    if(currentPreset===requestedPreset&&modelScene){
      requestId++;pendingPreset=null;loading=false;loadError=null;failedPreset=null;
      ready=Promise.resolve(true);return ready;
    }
    if(loading&&pendingPreset===requestedPreset)return ready;
    if(!retry&&failedPreset===requestedPreset){ready=Promise.resolve(false);return ready;}
    const token=++requestId,preset=requestedPreset,cached=sourceFor(preset);
    loadError=null;failedPreset=null;
    if(cached){
      install(cached,preset);pendingPreset=null;loading=false;ready=Promise.resolve(true);return ready;
    }
    loading=true;pendingPreset=preset;
    ready=library.load(preset).then(asset=>{
      if(disposed||token!==requestId||requestedPreset!==preset)return false;
      install(asset,preset);loading=false;pendingPreset=null;return true;
    }).catch(error=>{
      if(disposed||token!==requestId)return false;
      loading=false;pendingPreset=null;failedPreset=preset;
      loadError=error instanceof Error?error:new Error(String(error));
      return false;
    });
    return ready;
  }
  customize(config);
  return {
    root,body,customize,adoptPortalPose,clearPortalPose,prepareCustomization,
    modelOptions:AVATAR_MODELS.map(({value,label})=>({value,label})),
    get preset(){return currentPreset;},
    get requestedPreset(){return requestedPreset;},
    get loading(){return loading;},
    get error(){return loadError;},
    get ready(){return ready;},
    retry(){return customize(latestConfig,true);},
    get mixer(){return mixer;},
    get hairStyles(){
      const labels=currentPreset==='female'?{0:'Long bob',1:'Side-swept bob',2:'Ponytail'}:{};
      return hairChoices.map(object=>({value:object.userData.style_index,label:object.userData.style_label??labels[object.userData.style_index]??object.userData.style_id})).sort((a,b)=>a.value-b.value);
    },
    get animationState(){return currentName;},
    // The optional sixth argument keeps existing player/portrait/NPC callers
    // compatible. Flying is explicit: ordinary jumps never select Flight.
    update(dt,time,speed=0,airborne=false,wave=false,options={}){
      if(disposed)return;
      const seconds=Number.isFinite(dt)?clamp(dt,0,.1):0;
      const velocity=Number.isFinite(speed)?Math.max(0,speed):0;
      const flying=options?.flying===true,inAir=!!airborne||flying,waveRequested=wave===true;
      const waveSequence=Number.isSafeInteger(options?.waveSequence)?options.waveSequence:0;
      const newWave=waveRequested&&(!lastWave||(waveSequence>0&&waveSequence!==lastWaveSequence));
      const finished=currentAction?.loop===T.LoopOnce&&currentAction.time>=currentAction.getClip().duration;
      if(!waveRequested)waveConsumed=false;
      if(currentName==='Wave'&&finished)waveConsumed=true;
      if(newWave)waveConsumed=false;
      // A short gesture signal may finish its one-shot, but a held signal must
      // not freeze the final pose or automatically repeat the gesture.
      const keepWave=options?.boundedWave!==true&&currentName==='Wave'&&currentAction?.loop===T.LoopOnce&&!finished;
      let desired=avatarAnimationState(velocity,inAir,currentName,!waveConsumed&&(waveRequested||keepWave),actions.has('Wave'),actions.has('Jog'),{flying,flightSpeed:options?.flightSpeed,hasFlight:actions.has('Flight'),hasFlightTriplet:['FlightIdle','FlightMove','FlightFast'].every(name=>actions.has(name))});
      // Land only at rest. Starting any gait, jumping or flying takes priority
      // immediately; the animation never locks movement or changes root pose.
      const landing=actions.has('LandSoft')?'LandSoft':'Land';
      if(!inAir&&velocity<.12&&actions.has(landing)&&(lastAirborne||(currentName===landing&&!finished)))desired=landing;
      if(currentName==='Wave'&&desired!=='Wave')waveConsumed=true;
      if(desired==='Wave'&&newWave&&currentName==='Wave')currentAction?.reset().play();
      transition(desired);lastWave=waveRequested;lastAirborne=inAir;lastWaveSequence=waveSequence;
      if(currentAction){
        const measured=currentAction.getClip().userData?.locomotionSpeed;
        // Flight swing cadence follows measured 3D speed without changing physics or idle.
        const flightRate=clamp(.78+(Number.isFinite(options?.flightSpeed)?Math.max(0,options.flightSpeed):velocity)/45,.8,1.2);
        const rate=['FlightMove','FlightFast'].includes(currentName)?flightRate:currentName==='Wave'?Math.max(1,currentAction.getClip().duration/2):Number.isFinite(measured)&&measured>0?clamp(velocity/measured,.55,1.8):currentName==='Walk'?clamp(velocity/3.5,.65,1.7):currentName==='Run'?clamp(velocity/7.2,.8,1.4):1;
        currentAction.setEffectiveTimeScale(rate);
        if(['Walk','Jog','Run'].includes(currentName)){
          const cycle=Math.max(currentAction.getClip().duration,.001);
          for(const [name,action] of actions)if(action!==currentAction&&['Walk','Jog','Run'].includes(name))action.setEffectiveTimeScale(rate*action.getClip().duration/cycle);
        }
      }
      mixer.update(seconds);
      if(portalPose){
        const weight=portalPose.action.getEffectiveWeight();
        for(const {object,residual}of portalPose.nodes){
          object.updateMatrix();const matrix=portalResidualMatrix.copy(residual);
          for(let i=0;i<16;i++){const identity=i%5===0?1:0;matrix.elements[i]=identity+(matrix.elements[i]-identity)*weight;}
          object.matrix.multiply(matrix);object.matrixWorldNeedsUpdate=true;
        }
        if(mixer.time-portalPose.start>=.35)clearPortalPose();
      }shadow.visible=!inAir&&!!modelScene;
    },
    dispose(){
      if(disposed)return;transaction?.rollback();disposed=true;prepareId++;requestId++;loading=false;releaseModel();
      shadow.visible=false;shadow.geometry.dispose();shadow.material.dispose();body.userData.avatarUnavailable=true;
    },
  };
}

